import { Injectable, OnDestroy, inject } from '@angular/core';
import { Firestore, doc, docSnapshots } from '@angular/fire/firestore';
import { BehaviorSubject, Subscription, Observable, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UserService } from 'src/app/auth/services/user.service';
import { OrganizationConfig, OrganizationConfigs } from '../models/organization/config';
import { OrganizationService } from './organization.service';

@Injectable({
  providedIn: 'root',
})
export class OrganizationConfigService implements OnDestroy {
  private userService = inject(UserService);
  private firestore = inject(Firestore);
  private organizationService = inject(OrganizationService);

  private configSubject = new BehaviorSubject<OrganizationConfig>(new OrganizationConfig());
  private subscriptions: Subscription[] = [];

  constructor() {
    console.log('🏗️ [OrganizationConfigService] Constructor called');

    this.userService.orgUserAsObservable().subscribe((user) => {
      console.log('👤 [OrganizationConfigService] orgUser emitted:', {
        hasUser: !!user,
        orgId: user?.orgId,
        userName: user?.fullName,
      });

      if (user?.orgId) {
        const orgId = this.organizationService.getCurrentOrganizationId() || user.orgId;
        console.log('🔑 [OrganizationConfigService] Loading config for orgId:', orgId);
        this.loadConfig(orgId);
      } else {
        console.log('⚠️ [OrganizationConfigService] No orgId found, resetting config');
        this.resetConfig();
      }
    });
  }

  /** ✅ Returns the current organization config */
  getOrganizationConfig(): OrganizationConfig {
    return this.configSubject.value;
  }

  /** ✅ Observable to subscribe to config updates */
  configAsObservable(): Observable<OrganizationConfig> {
    return this.configSubject.asObservable();
  }

  private configLoadedSubject = new BehaviorSubject<boolean>(false);
  configLoaded$ = this.configLoadedSubject.asObservable();

  /**
   * Ensures config is loaded for the current organization
   * This method can be called by resolvers to trigger loading if needed
   */
  async ensureConfigLoaded(): Promise<void> {
    console.log('🔧 [OrganizationConfigService] ensureConfigLoaded called');

    // Check if already loaded
    if (this.configLoadedSubject.value) {
      console.log('✅ [OrganizationConfigService] Config already loaded');
      return;
    }

    // Get current org ID - try multiple sources
    let orgId = this.organizationService.getCurrentOrganizationId();
    console.log('📍 [OrganizationConfigService] OrgId from localStorage:', orgId);

    if (!orgId) {
      // Try to get from organization service
      const org = this.organizationService.getOrganization();
      if (org && org.id && org.id !== 'NO_ORG') {
        orgId = org.id;
        console.log('📍 [OrganizationConfigService] OrgId from OrganizationService:', orgId);
      }
    }

    if (!orgId) {
      // Try to get from user service
      const user = await firstValueFrom(this.userService.userAsObservable().pipe(filter((u) => !!u)));
      if (user?.orgId) {
        orgId = user.orgId;
        console.log('📍 [OrganizationConfigService] OrgId from UserService (UserFS):', orgId);
      } else if (user?.orgIds && user.orgIds.length > 0) {
        orgId = user.orgIds[0];
        console.log('📍 [OrganizationConfigService] Using first orgId from user.orgIds:', orgId);
      }
    }

    if (!orgId) {
      // Last resort - try orgUser
      const orgUser = await firstValueFrom(
        this.userService.orgUserAsObservable().pipe(filter((u) => !!u && !!u.orgId))
      );
      orgId = orgUser?.orgId;
      console.log('📍 [OrganizationConfigService] OrgId from OrgUser:', orgId);
    }

    if (!orgId) {
      console.error('❌ [OrganizationConfigService] No organization ID available after all attempts');
      throw new Error('No organization ID available for loading config');
    }

    console.log('📥 [OrganizationConfigService] Triggering config load for orgId:', orgId);
    this.loadConfig(orgId);
  }

  /** 🔹 Fetches all config documents at once and updates the observable */
  private loadConfig(orgId: string): void {
    console.log('🔄 [OrganizationConfigService] Starting loadConfig for orgId:', orgId);
    this.unsubscribeAll(); // Unsubscribe from any existing subscriptions first

    // Reset the loaded state when starting a new load
    this.configLoadedSubject.next(false);

    const configPaths = {
      activeModules: OrganizationConfigs.ActiveModules,
      integration: OrganizationConfigs.Integration,
      orderLabels: OrganizationConfigs.OrderLabels,
      orderStatusCodes: OrganizationConfigs.OrderStatusCodes,
      splitInboundOrderArticles: OrganizationConfigs.SplitInboundOrderArticles,
      warehouseOrdersManagement: OrganizationConfigs.WarehouseOrdersManagement,
      typesense: OrganizationConfigs.Typesense,
    };

    const fetchedConfigs = new Map<string, boolean>();
    Object.keys(configPaths).forEach((key) => fetchedConfigs.set(key, false));

    console.log('📋 [OrganizationConfigService] Config documents to fetch:', Object.keys(configPaths));

    Object.entries(configPaths).forEach(([configName, ConfigClass]) => {
      const configPath = `organizations/${orgId}/config/${configName}`;
      console.log(`📄 [OrganizationConfigService] Setting up subscription for: ${configPath}`);

      const configRef = doc(this.firestore, configPath);
      const sub = docSnapshots(configRef).subscribe((snapshot) => {
        console.log(`🔔 [OrganizationConfigService] Snapshot received for ${configName}:`, {
          exists: snapshot.exists(),
          path: configPath,
        });
        if (snapshot.exists()) {
          const updatedConfig = {
            ...this.configSubject.value,
            [configName]: new ConfigClass(snapshot.data()),
          };
          this.configSubject.next(updatedConfig);
        } else {
          console.warn(`OrganizationConfigService: No config found for ${configName}, using defaults`);
          // Still create a default instance even if document doesn't exist
          const updatedConfig = {
            ...this.configSubject.value,
            [configName]: new ConfigClass(),
          };
          this.configSubject.next(updatedConfig);
        }

        // Mark this config as fetched (whether it exists or not)
        fetchedConfigs.set(configName, true);

        const fetchedCount = [...fetchedConfigs.values()].filter((v) => v).length;
        const totalCount = fetchedConfigs.size;
        console.log(`📊 [OrganizationConfigService] Progress: ${fetchedCount}/${totalCount} configs fetched`);
        console.log('📊 [OrganizationConfigService] Fetched status:', Object.fromEntries(fetchedConfigs));

        // ✅ Check if ALL configs are fetched, then mark as fully loaded
        if ([...fetchedConfigs.values()].every((v) => v)) {
          console.log('✅ [OrganizationConfigService] All configs fetched! Setting configLoaded to true');
          this.configLoadedSubject.next(true); // ✅ Marks config as fully loaded
        }
      });

      this.subscriptions.push(sub);
    });
  }

  /** 🔹 Resets config when no valid orgId exists */
  private resetConfig(): void {
    this.unsubscribeAll();
    this.configSubject.next(new OrganizationConfig());
  }

  /** ✅ Unsubscribes from all active Firestore listeners */
  private unsubscribeAll(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }

  /** ✅ Ensures cleanup when the service is destroyed */
  ngOnDestroy(): void {
    this.unsubscribeAll();
  }
}
